<template>
  <div class="version-log-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span style="font-size: 18px; font-weight: bold;">版本更新日志管理</span>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar" style="margin-bottom: 20px; display: flex; align-items: center; flex-wrap: wrap; gap: 15px;">
        <el-input
          v-model="searchForm.createdBy"
          placeholder="创建人"
          prefix-icon="el-icon-user"
          clearable
          style="width: 200px;"
          @keyup.enter.native="handleSearch"
        ></el-input>
        <el-date-picker
          v-model="searchForm.createdTime"
          type="datetime"
          placeholder="起始时间（之后）"
          value-format="yyyy-MM-dd HH:mm:ss"
          clearable
          style="width: 220px;"
        ></el-date-picker>
        <div style="margin-left: auto; display: flex; gap: 10px;">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          <el-button type="success" icon="el-icon-plus" @click="handleAdd">添加日志</el-button>
        </div>
      </div>

      <!-- 数据表格 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        stripe
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column prop="versionCode" label="版本号" min-width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="medium">{{ scope.row.versionCode }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="minSupportedVersion" label="最低支持版本" min-width="120" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row.minSupportedVersion || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" min-width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusTag(scope.row.status)">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isForceUpdate" label="强制更新" min-width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isForceUpdate === 1 ? 'danger' : 'info'">
              {{ scope.row.isForceUpdate === 1 ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="newFeatures" label="新功能" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="optimizations" label="优化项" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="fixes" label="修复问题" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="releaseNotes" label="发布说明" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="downloadUrl" label="下载链接" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <el-link v-if="scope.row.downloadUrl" :href="scope.row.downloadUrl" target="_blank" type="primary">
              {{ scope.row.downloadUrl }}
            </el-link>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdBy" label="创建人" min-width="120" align="center"></el-table-column>
        <el-table-column prop="createdTime" label="创建时间" min-width="180" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createdTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="updatedTime" label="修改时间" width="180" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.updatedTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" icon="el-icon-delete" style="color: #f56c6c;" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container" style="margin-top: 20px; text-align: right;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="pagination.pageNum"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>

      <!-- 添加/编辑日志对话框 -->
      <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="750px" @close="resetForm">
        <el-form :model="logForm" :rules="rules" ref="logForm" label-width="110px">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="版本号" prop="versionCode">
                <el-input v-model="logForm.versionCode" placeholder="如: 1.0.0"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="最低版本" prop="minSupportedVersion">
                <el-input v-model="logForm.minSupportedVersion" placeholder="最低兼容版本"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="状态" prop="status">
                <el-select v-model="logForm.status" placeholder="请选择状态" style="width: 100%;">
                  <el-option label="已废弃" :value="0"></el-option>
                  <el-option label="当前版本" :value="1"></el-option>
                  <el-option label="历史版本" :value="2"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="强制更新" prop="isForceUpdate">
                <el-radio-group v-model="logForm.isForceUpdate">
                  <el-radio :label="1">是</el-radio>
                  <el-radio :label="0">否</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24">
              <el-form-item label="创建人" prop="createdBy">
                <el-input v-model="logForm.createdBy" placeholder="请输入创建人姓名"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="新功能" prop="newFeatures">
            <el-input type="textarea" v-model="logForm.newFeatures" placeholder="请输入新增的功能点" :rows="3"></el-input>
          </el-form-item>
          <el-form-item label="优化项" prop="optimizations">
            <el-input type="textarea" v-model="logForm.optimizations" placeholder="请输入优化的内容" :rows="3"></el-input>
          </el-form-item>
          <el-form-item label="修复问题" prop="fixes">
            <el-input type="textarea" v-model="logForm.fixes" placeholder="请输入修复的问题" :rows="3"></el-input>
          </el-form-item>
          <el-form-item label="发布说明" prop="releaseNotes">
            <el-input type="textarea" v-model="logForm.releaseNotes" placeholder="请输入发布说明" :rows="3"></el-input>
          </el-form-item>
          <el-form-item label="下载链接" :prop="isEdit ? '' : 'downloadUrl'">
            <el-input v-model="logForm.downloadUrl" placeholder="请输入APK下载链接" clearable></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="dialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="submitForm" :loading="submitLoading">确 定</el-button>
        </div>
      </el-dialog>
    </el-card>
  </div>
</template>

<script>
import { request } from '@/utils/http.js'

export default {
  name: 'VersionUpdateLogManagement',
  data() {
    return {
      loading: false,
      tableData: [],
      searchForm: {
        createdBy: '',
        createdTime: '',
        updatedTime: ''
      },
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      // 对话框相关
      dialogVisible: false,
      dialogTitle: '添加版本日志',
      submitLoading: false,
      isEdit: false,
      logForm: {
        id: null,
        versionCode: '',
        minSupportedVersion: '',
        status: 1,
        isForceUpdate: 0,
        newFeatures: '',
        optimizations: '',
        fixes: '',
        releaseNotes: '',
        createdBy: '',
        downloadUrl: ''
      },
      rules: {
        versionCode: [{ required: true, message: '请输入版本号', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }],
        isForceUpdate: [{ required: true, message: '请选择是否强制更新', trigger: 'change' }],
        createdBy: [{ required: true, message: '请输入创建人', trigger: 'blur' }],
        downloadUrl: [{ required: true, message: '请输入下载链接', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.fetchLogList()
  },
  methods: {
    async fetchLogList() {
      this.loading = true
      try {
        const params = new URLSearchParams()
        params.append('pageNum', this.pagination.pageNum)
        params.append('pageSize', this.pagination.pageSize)
        
        if (this.searchForm.createdBy) params.append('createdBy', this.searchForm.createdBy)
        if (this.searchForm.createdTime) params.append('createdTime', this.searchForm.createdTime)
        if (this.searchForm.updatedTime) params.append('updatedTime', this.searchForm.updatedTime)

        const queryString = params.toString()
        const url = `/admin/version-update-log/list?${queryString}`
        
        const response = await request(url)
        if (response && response.code === 200) {
          // 后端返回 Page 对象，包含 records 和 total
          const pageData = response.data || {}
          this.tableData = pageData.records || []
          this.pagination.total = pageData.total || 0
        } else {
          this.$message.error(response.msg || '获取版本日志失败')
        }
      } catch (error) {
        console.error('获取版本日志失败:', error)
        this.$message.error(error.message || '获取版本日志失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.pageNum = 1
      this.fetchLogList()
    },
    resetSearch() {
      this.searchForm = {
        createdBy: '',
        createdTime: '',
        updatedTime: ''
      }
      this.pagination.pageNum = 1
      this.fetchLogList()
    },
    handleCurrentChange(page) {
      this.pagination.pageNum = page
      this.fetchLogList()
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.pageNum = 1
      this.fetchLogList()
    },
    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '添加版本日志'
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = '编辑版本日志'
      this.dialogVisible = true
      // 深度克隆行数据到表单
      this.$nextTick(() => {
        this.logForm = JSON.parse(JSON.stringify(row))
      })
    },
    handleDelete(row) {
      this.$confirm('确定要删除该版本日志吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const url = `/admin/version-update-log/delete?id=${row.id}`
          const response = await request(url, {
            method: 'DELETE'
          })
          if (response && response.code === 200) {
            this.$message.success('删除成功')
            this.fetchLogList()
          } else {
            this.$message.error(response.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除版本日志失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    resetForm() {
      this.$refs.logForm.resetFields()
      this.logForm = {
        id: null,
        versionCode: '',
        minSupportedVersion: '',
        status: 1,
        isForceUpdate: 0,
        newFeatures: '',
        optimizations: '',
        fixes: '',
        releaseNotes: '',
        createdBy: '',
        downloadUrl: ''
      }
    },
    async submitForm() {
      this.$refs.logForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const url = this.isEdit ? '/admin/version-update-log/update' : '/admin/version-update-log/add'
            const method = this.isEdit ? 'PUT' : 'POST'
            
            // 提交前处理数据，移除创建时间和更新时间，由后端自动填充
            const submitData = { ...this.logForm }
            delete submitData.createdTime
            delete submitData.updatedTime
            
            const response = await request(url, {
              method: method,
              body: JSON.stringify(submitData)
            })
            
            if (response && response.code === 200) {
              this.$message.success(response.msg || (this.isEdit ? '修改成功' : '添加成功'))
              this.dialogVisible = false
              this.fetchLogList()
            } else {
              this.$message.error(response.msg || (this.isEdit ? '修改失败' : '添加失败'))
            }
          } catch (error) {
            console.error('保存版本日志失败:', error)
            this.$message.error(error.message || '保存失败')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    getStatusText(status) {
      const map = {
        0: '已废弃',
        1: '当前版本',
        2: '历史版本'
      }
      return map[status] || '未知'
    },
    getStatusTag(status) {
      const map = {
        0: 'info',
        1: 'success',
        2: 'warning'
      }
      return map[status] || ''
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.version-log-container {
}
.box-card {
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}
</style>
